import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { Button } from '@design-system/Button';
import { DataForm } from '@design-system/DataForm';
import { TextField } from '@design-system/DataForm/TextField';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSendReportMutation } from '@viome/viome-base/src/usecase/Misc/SendReportMutation';
import { cn } from '@/utils/styles';
import { useState, useEffect } from 'react';
import { useUserStore } from '@/hooks/useUserStore';

const schema = z.object({
  email: z.string().email(),
});

type FormData = z.infer<typeof schema>;

export const SendMyReportsPageReportFormSection = () => {
  const { mutate: sendReport, isPending } = useSendReportMutation();
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const user = useUserStore((state) => state.user);

  const form = useForm<FormData>({
    mode: 'onSubmit',
    resolver: zodResolver(schema),
    defaultValues: {
      email: user?.email || '',
    },
  });

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (isSuccess) {
      timeoutId = setTimeout(() => {
        setIsSuccess(false);
      }, 5000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isSuccess]);

  const onSubmit = (data: FormData) => {
    setError(null);

    sendReport(data, {
      onSuccess: () => {
        setIsSuccess(true);
        form.reset({ email: '' });
      },
      onError: (error) => {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to send report. Please try again.';
        setError(errorMessage);
        form.reset({ email: '' });
      },
    });
  };

  const buttonText = isPending
    ? 'Sending...'
    : isSuccess
      ? 'Sent!'
      : 'Send most recent report';

  return (
    <div>
      <DataForm form={form} onValid={onSubmit}>
        <TextField
          name="email"
          placeholder="Email"
          variant="secondary"
          onChange={() => {
            if (error) setError(null);
          }}
        />

        {error && <div className="text-red-500 text-sm mt-1 mb-2">{error}</div>}

        <Button
          type="submit"
          disabled={isPending}
          className={cn(
            'w-full',
            isSuccess && '!bg-background-success',
            isPending && 'opacity-50',
          )}
        >
          {buttonText}
        </Button>
      </DataForm>
    </div>
  );
};
